import { FC, ReactNode, useEffect } from 'react';
import { Svg, Tab, Tabs, Tooltip } from '@hopskipdrive/react-components';
import { TabProps } from '@mui/material';
import styled from 'styled-components';

import { Accordion } from '../../../shared/components/Accordion';
import { OrganizerIcon } from '../../../shared/components/Icons';
import { colorTokens } from '../../../shared/components/variables';
import { HEADING_MAX_HEIGHT } from '../PageHeading/constants';

interface InfoNavDrawerProps {
  isDrawerOpen: boolean;
  handleOpenDrawer: (isOpen: boolean) => void;
  selectedTab: string;
  handleSelectTab: (tab: string) => void;
  children: ReactNode;
}

export const InfoNavDrawer: FC<InfoNavDrawerProps> = ({
  isDrawerOpen,
  handleOpenDrawer,
  selectedTab,
  handleSelectTab,
  children,
}) => {
  const handleTabClick = (event, tab: string) => {
    event.stopPropagation();

    handleSelectTab(tab);
    !isDrawerOpen && handleOpenDrawer(true);
  };

  useEffect(() => {
    if (isDrawerOpen && !selectedTab) {
      handleSelectTab('driver-details');
    }
  }, [isDrawerOpen, selectedTab, handleSelectTab]);

  useEffect(() => {
    if (!isDrawerOpen && selectedTab) {
      handleSelectTab('');
    }
  }, [isDrawerOpen, selectedTab, handleSelectTab]);

  return (
    <Accordion data-testid="info-nav-drawer" square={true} expanded={isDrawerOpen}>
      <Accordion.Header
        expandIcon={<Svg icon="expand-less-filled" style={{ color: colorTokens.primary.main }} />}
        sx={{
          width: '100%',
          justifyContent: 'center',
        }}
        onClick={() => handleOpenDrawer(!isDrawerOpen)}
      >
        <Tabs data-testid="info-nav-tabs" value={selectedTab} style={{ pointerEvents: 'all' }}>
          <TabWithTooltip
            label={<Svg icon="directions-car-filled" />}
            tooltipTitle="Driver"
            value="driver-details"
            onClick={(e) => handleTabClick(e, 'driver-details')}
          />
          <TabWithTooltip
            label={<Svg icon="sidebar_riders" />}
            tooltipTitle="Passenger"
            value="riders-details"
            onClick={(e) => handleTabClick(e, 'riders-details')}
          />
          <TabWithTooltip
            label={<OrganizerIcon />}
            tooltipTitle="Organizer"
            value="organizer-details"
            onClick={(e) => handleTabClick(e, 'organizer-details')}
          />
          <TabWithTooltip
            label={<Svg icon="timeline" />}
            tooltipTitle="Ride Overview"
            value="ride-overview"
            onClick={(e) => handleTabClick(e, 'ride-overview')}
          />
          <TabWithTooltip
            label={<Svg icon="more-horizontal" />}
            tooltipTitle="More"
            value="more"
            onClick={(e) => handleTabClick(e, 'more')}
          />
        </Tabs>
      </Accordion.Header>
      <Accordion.Body>{children}</Accordion.Body>
    </Accordion>
  );
};

export const InfoNavContent = styled.div`
  overflow: auto;
  width: 100%;
  padding: 16px;

  height: calc(100vh);
  transition: all 3s ease-in;
  background-color: #e4eaf0;

  @media (max-width: 1440px) {
    height: calc(100vh - ${HEADING_MAX_HEIGHT}px);
  }
`;

interface TabWithTooltipProps extends TabProps {
  tooltipTitle: string;
}

const TabWithTooltip: FC<TabWithTooltipProps> = ({ tooltipTitle, label, ...restTabProps }) => {
  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
          },
        },
      }}
    >
      <Tab label={label} {...restTabProps} />
    </Tooltip>
  );
};
